'use client';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import throttle from 'lodash.throttle';

import Button from '../Button';
import Input from '../Input';
import LoginModal from '../LoginModal';
import HeaderPortrait from './HeaderPortrait';
import HeaderSelect from './HeaderSelect';
import MenuContent from './MenuContent';
import MenuOptions from './MenuOptions';

import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { IHeaderDictionary } from '@/dictionaries/components/header/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { ISendToFriendsFormDictionary } from '@/dictionaries/components/SendToFriendsForm/types';
import { IUserDropDownDictionary } from '@/dictionaries/components/userDropDown/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { ISlugDictionary } from '@/dictionaries/utils/slugs/types';
import { IURLDictionary } from '@/dictionaries/utils/url/types';

import { IContactPageDictionary } from '@/dictionaries/pages/contacts/types';
import useGlobalContext from '@/hooks/useGlobalContext';
import useUserContext from '@/hooks/useUserContext';
import { cn } from '@/lib/utils';
import { VillasCount } from '@/services/types';
import { customLink } from '@/utils';
import { IoSearchSharp } from 'react-icons/io5';
import { Locale } from '../../../i18n.config';
import logoBlue from '../../../public/logo-desktop.svg';
import logoWhite from '../../../public/white-desktop-logo.svg';
import UserAvatar from '../UserAvatar';
import UserDropDown from '../UserDropDown';

interface IHeaderProps {
  lang: Locale;
  dict: IHeaderDictionary;
  urlDict: IURLDictionary;
  modalsDict: IModalsDictionary;
  enquiryFormDict: IEnquiryFormDictionary;
  sendToFriendFormDict: ISendToFriendsFormDictionary;
  countriesDict: ICountriesDictionary;
  datePickerDict: IDatePickerDictonary;
  slugsDict: ISlugDictionary;
  userDropDownDict: IUserDropDownDictionary;
  regionVillasCount: VillasCount[];
  contactUsDict: IContactPageDictionary;
}

const Header: FC<IHeaderProps> = ({
  lang,
  dict,
  enquiryFormDict,
  sendToFriendFormDict,
  countriesDict,
  contactUsDict,
  datePickerDict,
  modalsDict,
  urlDict,
  slugsDict,
  userDropDownDict,
  regionVillasCount,
}) => {
  const router = useRouter();
  const pathname = usePathname();

  const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
  const [showVilla, setShowVilla] = useState<boolean>(false);
  const [showGuide, setShowGuide] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  const { isLogged, user, userGrant } = useUserContext();
  const { isHeaderActive, setIsHeaderActive } = useGlobalContext();

  const searchInputRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, setValue } = useForm();

  const isHomePage = ['/', '/it', '/fr', '/de'].includes(pathname);

  const isActive = useMemo(
    () => (isHomePage ? isScrolled || isHeaderActive : true),
    [isHeaderActive, isHomePage, isScrolled],
  );

  const handleSearch = handleSubmit((data) => {
    //TODO: search villas
    router.push(
      `${lang === 'en' ? '' : `/${lang}`}${dict.villaCards[0].path}?q=${
        data.search
      }`,
    );
    setValue('search', '');
    setShowSearchInput(false);
  });

  const handleSearchInput = () => {
    setShowSearchInput(!showSearchInput);
    setIsHeaderActive(true);
  };

  // useEffect(() => {
  //   const handleClose = (event: MouseEvent) => {
  //     if (
  //       searchInputRef.current &&
  //       !searchInputRef.current.contains(event.target as Node)
  //     ) {
  //       setShowSearchInput(false);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClose);

  //   return () => {
  //     document.removeEventListener('mousedown', handleClose);
  //   };
  // }, [searchInputRef]);

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.querySelector('input')?.focus();
    }
  }, [showSearchInput]);

  useEffect(() => {
    if (showVilla || showGuide) {
      setIsHeaderActive(true);
    } else if (!showVilla && !showGuide && !openMenu) {
      setIsHeaderActive(false);
    }
  }, [showVilla, showGuide, setIsHeaderActive, openMenu]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    const throttledScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  return (
    <>
      <header
        className={cn(
          isActive ? 'bg-white text-black/90' : 'bg-custom-gradient text-white',
          'sticky top-0 flex desktop:h-[100px] items-center w-full px-0 desktop:px-12 desktop:max-w-full z-[999]',
          {
            hidden:
              pathname.includes('resetting') || pathname.includes('login'),
            'z-[1000]': openMenu,
          },
        )}>
        <div className="relative justify-between items-center w-full hidden desktop:flex desktop:shrink-0">
          <Link
            className="leading-[2] text-xl pt-1 left-20 top-7 z-[200] w-[225px]"
            href={customLink(lang, '/')}>
            <figure className="inline-block">
              <Image
                priority={true}
                src={isActive ? logoBlue : logoWhite}
                alt="logo"
                width={225}
                height={40}
              />
            </figure>
          </Link>

          <MenuOptions
            dict={dict}
            lang={lang}
            pathname={pathname}
            setShowVilla={setShowVilla}
            setShowGuide={setShowGuide}
          />

          <div className="flex items-center">
            <HeaderSelect
              dict={dict}
              urlDict={urlDict}
              slugsDict={slugsDict}
              lang={lang}
              countriesDict={countriesDict}
              contactUsDict={contactUsDict}
              phone={dict.nav.phone}
              modalsDict={modalsDict}
              enquiryFormDict={enquiryFormDict}
              sendToFriendFormDict={sendToFriendFormDict}
              datePickerDict={datePickerDict}
              isActive={isActive}
            />
            <button
              id="search-villages"
              aria-label="Open village search input"
              className={cn(
                'cursor-pointer px-[13px] py-[10px] text-xl xl:px-[calc(0.9375rem+10px)] transition-colors duration-300 ease-in-out',
                isActive ? 'text-black/90' : 'text-white',
              )}
              onClick={handleSearchInput}>
              <IoSearchSharp />
            </button>
            {!isLogged ? (
              <LoginModal lang={lang} dict={modalsDict} isHeader />
            ) : (
              <UserDropDown dict={userDropDownDict} lang={lang}>
                <UserAvatar.Container lang={lang} userGrant={userGrant}>
                  <UserAvatar.Root user={user} userGrant={userGrant} />
                </UserAvatar.Container>
              </UserDropDown>
            )}
          </div>
        </div>

        <HeaderPortrait
          dict={dict}
          urlDict={urlDict}
          modalsDict={modalsDict}
          enquiryFormDict={enquiryFormDict}
          sendToFriendFormDict={sendToFriendFormDict}
          countriesDict={countriesDict}
          contactUsDict={contactUsDict}
          datePickerDict={datePickerDict}
          slugsDict={slugsDict}
          setShowVilla={setShowVilla}
          setShowGuide={setShowGuide}
          handleSearch={handleSearch}
          register={register}
          lang={lang}
          isActive={isActive}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
        />
      </header>
      <div
        ref={searchInputRef}
        className={cn(
          `fixed left-0 right-0 top-0 w-full h-[100px] bg-white shadow-lg z-[999] transition-all duration-500 ease-in-out`,
          showSearchInput
            ? 'block opacity-100 translate-y-0'
            : 'hidden opacity-0 -translate-y-4',
        )}>
        {showSearchInput && (
          <div className="container mx-auto h-full flex items-center justify-between px-12">
            <div className="w-[225px]">
              <figure className="inline-block">
                <Image
                  priority={true}
                  src={logoBlue}
                  alt="logo"
                  width={225}
                  height={40}
                />
              </figure>
            </div>
            <form
              className="desktop:max-w-[1200px] flex-1 flex mx-4"
              onSubmit={handleSearch}>
              <Input.Container className="w-full">
                <Input.Root
                  register={register}
                  name="search"
                  placeholder={dict.search}
                  className="placeholder:text-gray-400 placeholder:text-sm w-full"
                  autoFocus
                />
              </Input.Container>
              <div className="flex items-center gap-2 pl-5">
                <Button className="w-auto h-[44px] px-[10px]">
                  <span className="text-lg m-[5px]">
                    <IoSearchSharp width={40} />
                  </span>
                </Button>
                <Button
                  className="border-none w-auto h-[44px] px-[10px] bg-white text-gray-500"
                  onClick={() => {
                    setShowSearchInput(false);
                    setIsHeaderActive(false);
                  }}>
                  <span className="uppercase text-sm font-medium">
                    {dict.searchClose}
                  </span>
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>

      <MenuContent
        dict={dict}
        lang={lang}
        showVilla={showVilla}
        showGuide={showGuide}
        setShowVilla={setShowVilla}
        setShowGuide={setShowGuide}
        regionVillasCount={regionVillasCount}
      />
    </>
  );
};

export default Header;
